import { Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle, XCircle, CircleNotch, ArrowCounterClockwise } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { resolveGroupPaymentView, type GroupPaymentState } from "@/lib/payment-result-view";
import type { EnrollmentReturn } from "@/lib/payment-return-url";
import { useConfirmMonriReturn } from "@/hooks/useConfirmMonriReturn";

type EnrollmentStatusResp = GroupPaymentState;

const POLL_MS = 2000;
const POLL_WINDOW_MS = 30000;

/**
 * Result page after the Monri form for a group-therapy enrollment. Rendered by
 * both the path-based return route and the legacy query-based one; the props
 * are already validated (see src/lib/payment-return-url.ts).
 */
export function GroupPaymentResult({ outcome: status, enrollmentId: enrollment }: EnrollmentReturn) {
  const { t } = useTranslation();
  const [startedAt] = useState(() => Date.now());
  const queryClient = useQueryClient();

  // Poll DB truth (webhook result) rather than trusting the outcome in the return URL.
  const { data } = useQuery<EnrollmentStatusResp>({
    queryKey: ["enrollment-payment-status", enrollment],
    queryFn: async () => {
      const res = await fetch(
        `/api/group-therapies/enrollments/${enrollment}/payment-status`
      );
      if (!res.ok) throw new Error("enrollment-payment-status");
      return res.json() as Promise<EnrollmentStatusResp>;
    },
    enabled: !!enrollment,
    retry: false,
    refetchInterval: (query) => {
      const d = query.state.data;
      // Terminal: seat resolved either way, or the charge failed.
      if (
        d &&
        (d.cancelled ||
          d.paymentStatus === "paid" ||
          d.paymentStatus === "refunded" ||
          d.paymentStatus === "failed")
      ) {
        return false;
      }
      if (Date.now() - startedAt > POLL_WINDOW_MS) return false;
      return POLL_MS;
    },
  });

  // Monri signs the success redirect; confirming it covers a lost callback.
  useConfirmMonriReturn(status === "success", () =>
    queryClient.invalidateQueries({ queryKey: ["enrollment-payment-status", enrollment] }),
  );

  // Monri never calls back for a cancelled attempt; the cancel_url redirect is
  // the only signal. Release the pending seat once so it is free again.
  const [releaseSent, setReleaseSent] = useState(false);
  useEffect(() => {
    if (releaseSent || status !== "cancelled" || !enrollment) return;
    if (!data || data.cancelled || data.paymentStatus !== "pending") return;
    setReleaseSent(true);
    fetch(`/api/group-therapies/enrollments/${enrollment}/abandon-payment`, { method: "POST" })
      .then(() => queryClient.invalidateQueries({ queryKey: ["enrollment-payment-status", enrollment] }))
      .catch(() => {});
  }, [releaseSent, status, enrollment, data, queryClient]);

  const view = resolveGroupPaymentView(data, status);

  const tone =
    view === "success"
      ? "success"
      : view === "processing"
        ? "processing"
        : view === "seatUnavailable"
          ? "refund"
          : "error";
  const bg =
    tone === "success"
      ? "oklch(92% 0.05 155)"
      : tone === "processing"
        ? "oklch(93% 0.03 250)"
        : tone === "refund"
          ? "oklch(93% 0.04 80)"
          : "oklch(92% 0.05 25)";

  const title =
    view === "success"
      ? t("booking.groupPaymentSuccessTitle")
      : view === "processing"
        ? t("booking.groupPaymentProcessingTitle")
        : view === "seatUnavailable"
          ? t("booking.groupPaymentSeatUnavailableTitle")
          : view === "cancelled"
            ? t("booking.paymentCancelledTitle")
            : t("booking.groupPaymentFailedTitle");

  const desc =
    view === "success"
      ? t("booking.groupPaymentSuccessDesc")
      : view === "processing"
        ? t("booking.groupPaymentProcessingDesc")
        : view === "seatUnavailable"
          ? t("booking.groupPaymentSeatUnavailableDesc")
          : view === "cancelled"
            ? t("booking.groupPaymentCancelledDesc", {
                defaultValue:
                  "Niste dovršili plaćanje pa prijava nije potvrđena. Možete pokušati ponovo iz detalja grupne terapije.",
              })
            : t("booking.groupPaymentFailedDesc");

  return (
    <div className="flex min-h-[60vh] items-center justify-center px-4">
      <div className="max-w-sm text-center">
        <div
          className="mx-auto flex size-16 items-center justify-center rounded-full"
          style={{ background: bg }}
        >
          {view === "success" ? (
            <CheckCircle className="size-8" style={{ color: "oklch(55% 0.15 155)" }} />
          ) : view === "processing" ? (
            <CircleNotch className="size-8 animate-spin" style={{ color: "oklch(50% 0.13 250)" }} />
          ) : view === "seatUnavailable" ? (
            <ArrowCounterClockwise className="size-8" style={{ color: "oklch(55% 0.13 80)" }} />
          ) : (
            <XCircle className="size-8" style={{ color: "oklch(55% 0.15 25)" }} />
          )}
        </div>
        <h2
          className="mt-5 text-xl font-bold text-foreground"
          style={{ fontFamily: "var(--font-heading)" }}
        >
          {title}
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">{desc}</p>

        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <Link to="/dashboard/client/group-therapies">
            <Button>{t("booking.myGroupTherapies")}</Button>
          </Link>
          <Link to="/grupne-terapije">
            <Button variant="outline">{t("booking.allGroupTherapies")}</Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
